import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { CheckCircle, AlertCircle, X, AlertTriangle } from 'lucide-react';
import { VideoItem } from '../types';

interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'info';
  onClose: () => void;
}

export const Toast: React.FC<ToastProps> = ({ message, type = 'success', onClose }) => {
  React.useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const icons = {
    success: <CheckCircle className="text-emerald-500" size={20} />,
    error: <AlertCircle className="text-rose-500" size={20} />,
    info: <AlertCircle className="text-blue-500" size={20} />,
  };

  const bgColors = {
    success: 'bg-emerald-50 border-emerald-100',
    error: 'bg-rose-50 border-rose-100',
    info: 'bg-blue-50 border-blue-100',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className={`fixed bottom-24 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-3 px-6 py-3 rounded-2xl border shadow-xl ${bgColors[type]} min-w-[300px] max-w-[90vw]`}
    >
      {icons[type]}
      <p className="text-sm font-bold text-stone-800 flex-1">{message}</p>
      <button onClick={onClose} className="text-stone-400 hover:text-stone-600">
        <X size={18} />
      </button>
    </motion.div>
  );
};

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'info';
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'info'
}) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[110]">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white w-full max-w-sm rounded-[32px] p-8 shadow-2xl"
        >
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 ${type === 'danger' ? 'bg-rose-50 text-rose-500' : 'bg-blue-50 text-blue-500'}`}>
            <AlertTriangle size={32} />
          </div>
          <h3 className="text-xl font-black text-stone-800 mb-2">{title}</h3>
          <p className="text-stone-500 text-sm leading-relaxed mb-8">{message}</p>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="flex-1 px-6 py-3 rounded-2xl font-bold text-stone-400 hover:bg-stone-50 transition-colors"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className={`flex-1 px-6 py-3 rounded-2xl font-bold text-white shadow-lg transition-all active:scale-95 ${type === 'danger' ? 'bg-rose-500 shadow-rose-200 hover:bg-rose-600' : 'bg-emerald-600 shadow-emerald-200 hover:bg-emerald-700'}`}
            >
              {confirmText}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export function VideoSection({ videos, placement, t }: { videos: VideoItem[], placement: string, t: any }) {
  const filteredVideos = videos.filter(v => (v.placement || ['videos']).includes(placement));
  if (filteredVideos.length === 0) return null;

  return (
    <div className="space-y-4 my-6">
      <div className="flex justify-between items-center px-1">
        <h3 className="text-lg font-bold text-stone-800">{t.relatedVideos || 'Related Videos'}</h3>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-4 no-scrollbar -mx-1 px-1">
        {filteredVideos.map(video => (
          <div key={video.id} className="flex-shrink-0 w-72 bg-white rounded-2xl overflow-hidden border border-black/5 shadow-sm group">
             <div className="aspect-video bg-stone-100 relative overflow-hidden">
                <iframe 
                  src={video.url} 
                  className="w-full h-full border-0" 
                  title={video.title}
                  allowFullScreen
                />
              </div>
              <div className="p-3">
                <h4 className="font-bold text-sm text-stone-800 line-clamp-1">{video.title}</h4>
              </div>
          </div>
        ))}
      </div>
    </div>
  );
}
